import { Injectable, inject } from '@angular/core';
import { catchError, firstValueFrom, of } from 'rxjs';
import { FilesService } from './files.service';
import { OrgSettings, OrgSettingsService } from './org-settings.service';
import { assetUrlCandidates } from '../config/app-base';
import { SheetImageSource, loadFirstSheetImage, loadSheetImage } from '../../shared/utils/styled-workbook.util';

/** The nibni mark that goes in the "Powered by" slot of every branded export. */
const NIBNI_SHEET_LOGO_ASSET = 'assets/images/brand/logo/nibni-logo-black.png';

export interface SheetImageBox {
    maxWidth: number;
    maxHeight: number;
}

/**
 * Branding for the styled .xlsx exports, pulled from the two places the app already
 * keeps it: company settings and a project's offer/cover images.
 *
 * Every method is best-effort and resolves to null rather than throwing — a missing
 * logo or a 403 on `/org/settings` must degrade the masthead to text, never fail
 * the export the user asked for.
 */
@Injectable({ providedIn: 'root' })
export class SheetBrandingService {
    private orgSettings = inject(OrgSettingsService);
    private files = inject(FilesService);

    /** Company settings, or null when the call fails or the actor cannot read them. */
    loadOrgSettings(): Promise<OrgSettings | null> {
        return firstValueFrom(this.orgSettings.getSettings().pipe(catchError(() => of(null))));
    }

    /**
     * One image, from a file id first and a hosted URL second.
     *
     * The order matters: a file id is fetched through our own origin, so the bytes
     * are readable. The Stratus `*_url` forms would need CORS to be *read* rather
     * than just rendered in an `<img>`, which is why they are the fallback.
     */
    async loadImage(
        fileId: string | null | undefined,
        url: string | null | undefined,
        box: SheetImageBox
    ): Promise<SheetImageSource | null> {
        const id = String(fileId || '').trim();
        if (id) {
            const blob = await firstValueFrom(
                this.files.downloadPrivateFile(id).pipe(catchError(() => of(null)))
            );
            if (blob) {
                const loaded = await loadSheetImage(blob, box);
                if (loaded) return loaded;
            }
        }
        const href = String(url || '').trim();
        return href ? loadSheetImage(href, box) : null;
    }

    /** First candidate that yields usable bytes, in the order given. */
    async loadFirstImage(
        candidates: readonly { fileId?: string | null; url?: string | null }[],
        box: SheetImageBox
    ): Promise<SheetImageSource | null> {
        for (const candidate of candidates) {
            const loaded = await this.loadImage(candidate?.fileId, candidate?.url, box);
            if (loaded) return loaded;
        }
        return null;
    }

    /**
     * The bundled nibni mark. Built assets live under the Catalyst mount in
     * production and at the root under `ng serve`, so both paths are tried.
     */
    loadNibniLogo(box: SheetImageBox): Promise<SheetImageSource | null> {
        return loadFirstSheetImage(assetUrlCandidates(NIBNI_SHEET_LOGO_ASSET), box);
    }
}
